const POSTAL_LOOKUP_URL = 'https://viacep.com.br/ws';
const REVERSE_GEOCODING_URL = 'https://nominatim.openstreetmap.org/reverse';

class SpecializedUtilityBar extends HTMLElement {
  #initialized = false;
  #cleanup = [];
  #announcementToggle;
  #announcementPanel;
  #locationToggle;
  #locationPanel;
  #postalInput;
  #postalSubmit;
  #postalLabel;
  #status;

  connectedCallback() {
    if (this.#initialized) return;
    this.#initialized = true;

    this.#announcementToggle = this.querySelector('[data-sp-announcements-toggle]');
    this.#announcementPanel = this.querySelector('[data-sp-announcements-panel]');
    this.#locationToggle = this.querySelector('[data-sp-location-toggle]');
    this.#locationPanel = this.querySelector('[data-sp-location-panel]');
    this.#postalInput = this.querySelector('[data-sp-postal-input]');
    this.#postalSubmit = this.querySelector('[data-sp-postal-submit]');
    this.#postalLabel = this.querySelector('[data-sp-postal-label]');
    this.#status = this.querySelector('[data-sp-location-status]');

    this.#buildAnnouncementList();
    this.#restorePostalCode();
    this.#bindEvents();
  }

  disconnectedCallback() {
    this.#cleanup.forEach((cleanup) => cleanup());
    this.#cleanup = [];
    this.#initialized = false;
  }

  #bindEvents() {
    if (this.#announcementToggle && this.#announcementPanel) {
      this.#listen(this.#announcementToggle, 'click', () => {
        this.#togglePanel(this.#announcementPanel, this.#announcementToggle);
      });
    }

    if (this.#locationToggle && this.#locationPanel) {
      this.#listen(this.#locationToggle, 'click', () => {
        const willOpen = this.#locationPanel.hidden;
        this.#togglePanel(this.#locationPanel, this.#locationToggle);
        if (willOpen) window.requestAnimationFrame(() => this.#postalInput?.focus());
      });
    }

    this.querySelectorAll('[data-sp-panel-close]').forEach((button) => {
      this.#listen(button, 'click', () => this.#closePanels());
    });

    if (this.#postalInput) {
      this.#listen(this.#postalInput, 'input', () => {
        this.#postalInput.value = this.#formatPostalCode(this.#postalInput.value);
        this.#updateSubmitState();
        this.#setStatus('');
      });
    }

    const form = this.querySelector('[data-sp-postal-form]');
    if (form) {
      this.#listen(form, 'submit', (event) => {
        event.preventDefault();
        this.#lookupPostalCode(this.#postalInput?.value || '');
      });
    }

    const geolocateButton = this.querySelector('[data-sp-geolocate]');
    if (geolocateButton) {
      this.#listen(geolocateButton, 'click', () => this.#useCurrentLocation(geolocateButton));
    }

    this.#listen(document, 'pointerdown', (event) => {
      if (event.target instanceof Node && !this.contains(event.target)) this.#closePanels();
    });

    this.#listen(document, 'keydown', (event) => {
      if (event.key !== 'Escape') return;
      const returnTarget = this.#locationPanel && !this.#locationPanel.hidden
        ? this.#locationToggle
        : this.#announcementToggle;
      this.#closePanels();
      returnTarget?.focus();
    });
  }

  #buildAnnouncementList() {
    const list = this.querySelector('[data-sp-announcement-list]');
    if (!list) return;

    list.replaceChildren();
    this.querySelectorAll('[data-sp-announcement-title]').forEach((slide) => {
      const title = slide.dataset.spAnnouncementTitle?.trim();
      if (!title) return;

      const item = document.createElement('article');
      item.className = 'sp-utility__announcement-item';

      const heading = document.createElement('h3');
      heading.className = 'sp-utility__announcement-title';
      heading.textContent = title;
      item.append(heading);

      const description = slide.dataset.spAnnouncementDescription?.trim();
      if (description) {
        const paragraph = document.createElement('p');
        paragraph.className = 'sp-utility__announcement-description';
        paragraph.textContent = description;
        item.append(paragraph);
      }

      const url = slide.dataset.spAnnouncementLink?.trim();
      if (url) {
        const link = document.createElement('a');
        link.className = 'sp-utility__announcement-cta';
        link.href = url;
        link.textContent = slide.dataset.spAnnouncementCta?.trim() || 'Saiba mais';
        item.append(link);
      }

      list.append(item);
    });
  }

  #restorePostalCode() {
    let postalCode = '';

    try {
      postalCode = localStorage.getItem(this.dataset.storageKey || 'multibike-postal-code') || '';
    } catch {
      postalCode = '';
    }

    postalCode ||= this.dataset.defaultPostalCode || '';
    const formatted = this.#formatPostalCode(postalCode);

    if (this.#postalInput && this.#postalDigits(formatted).length === 8) {
      this.#postalInput.value = formatted;
    }

    this.#setPostalLabel(formatted);
    this.#updateSubmitState();
  }

  async #lookupPostalCode(value) {
    const digits = this.#postalDigits(value);
    if (digits.length !== 8) {
      this.#setStatus(this.dataset.messageInvalid || 'Digite um CEP válido com 8 números.');
      return;
    }

    this.#setLoading(true);
    this.#setStatus(this.dataset.messageSearching || 'Localizando seu CEP...');

    try {
      const response = await fetch(`${POSTAL_LOOKUP_URL}/${digits}/json/`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('Postal lookup failed');

      const address = await response.json();
      if (address.erro) {
        this.#setStatus(this.dataset.messageInvalid || 'Digite um CEP válido com 8 números.');
        return;
      }

      const formatted = this.#formatPostalCode(address.cep || digits);
      const city = [address.localidade, address.uf].filter(Boolean).join(' - ');
      this.#savePostalCode(formatted, city ? `Localização definida: ${city}.` : 'CEP definido com sucesso.');
    } catch {
      const formatted = this.#formatPostalCode(digits);
      this.#savePostalCode(formatted, 'CEP salvo. A consulta do endereço está temporariamente indisponível.');
    } finally {
      this.#setLoading(false);
    }
  }

  #useCurrentLocation(button) {
    if (this.dataset.geolocationEnabled !== 'true' || !navigator.geolocation) {
      this.#setStatus(this.dataset.messageLocationError || 'Não foi possível identificar seu CEP.');
      return;
    }

    button.disabled = true;
    this.#setStatus(this.dataset.messageSearching || 'Localizando seu CEP...');

    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        try {
          const url = new URL(REVERSE_GEOCODING_URL);
          url.searchParams.set('format', 'jsonv2');
          url.searchParams.set('lat', String(coords.latitude));
          url.searchParams.set('lon', String(coords.longitude));
          url.searchParams.set('addressdetails', '1');
          url.searchParams.set('zoom', '18');
          url.searchParams.set('accept-language', 'pt-BR');

          const response = await fetch(url, { headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error('Reverse geocoding failed');

          const result = await response.json();
          const postalCode = result.address?.postcode || '';
          if (this.#postalDigits(postalCode).length !== 8) throw new Error('Postal code missing');

          if (this.#postalInput) this.#postalInput.value = this.#formatPostalCode(postalCode);
          await this.#lookupPostalCode(postalCode);
        } catch {
          this.#setStatus(this.dataset.messageLocationError || 'Não foi possível identificar seu CEP. Digite-o manualmente.');
        } finally {
          button.disabled = false;
        }
      },
      () => {
        button.disabled = false;
        this.#setStatus(this.dataset.messageLocationError || 'Não foi possível identificar seu CEP. Digite-o manualmente.');
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 900000 }
    );
  }

  #savePostalCode(postalCode, message) {
    const formatted = this.#formatPostalCode(postalCode);

    try {
      localStorage.setItem(this.dataset.storageKey || 'multibike-postal-code', formatted);
    } catch {
      // Private browsing or restrictive storage settings should not block the feature.
    }

    if (this.#postalInput) this.#postalInput.value = formatted;
    this.#setPostalLabel(formatted);
    this.#updateSubmitState();
    this.#setStatus(message);

    this.dispatchEvent(new CustomEvent('multibike:postal-code-change', {
      bubbles: true,
      detail: { postalCode: formatted },
    }));
  }

  #setPostalLabel(postalCode) {
    if (!this.#postalLabel) return;
    const formatted = this.#formatPostalCode(postalCode);
    this.#postalLabel.textContent = this.#postalDigits(formatted).length === 8
      ? formatted
      : (this.dataset.emptyLocationLabel || 'Insira seu CEP');
  }

  #updateSubmitState() {
    if (!this.#postalSubmit) return;
    this.#postalSubmit.disabled = this.#postalDigits(this.#postalInput?.value || '').length !== 8;
  }

  #setLoading(loading) {
    if (this.#postalInput) this.#postalInput.disabled = loading;
    if (this.#postalSubmit) this.#postalSubmit.disabled = loading;
  }

  #setStatus(message) {
    if (this.#status) this.#status.textContent = message;
  }

  #togglePanel(panel, trigger) {
    const shouldOpen = panel.hidden;
    this.#closePanels();
    panel.hidden = !shouldOpen;
    trigger.setAttribute('aria-expanded', String(shouldOpen));
  }

  #closePanels() {
    if (this.#announcementPanel) this.#announcementPanel.hidden = true;
    if (this.#locationPanel) this.#locationPanel.hidden = true;
    this.#announcementToggle?.setAttribute('aria-expanded', 'false');
    this.#locationToggle?.setAttribute('aria-expanded', 'false');
  }

  #postalDigits(value) {
    return String(value).replace(/\D/g, '').slice(0, 8);
  }

  #formatPostalCode(value) {
    const digits = this.#postalDigits(value);
    return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
  }

  #listen(target, eventName, listener, options) {
    target.addEventListener(eventName, listener, options);
    this.#cleanup.push(() => target.removeEventListener(eventName, listener, options));
  }
}

if (!customElements.get('specialized-utility-bar')) {
  customElements.define('specialized-utility-bar', SpecializedUtilityBar);
}
